import Icon from '@/components/Icon/Icon'
import clsx from 'clsx'
import PropTypes from 'prop-types'
import { useEffect, useRef } from 'react'

const Checkbox = ({
  id,
  checked = false,
  indeterminate = false,
  onChange,
  name,
  label,
  disabled = false,
  addClass,
  backgroundColor,
  checkIcon = <Icon name="check" size="small" style="outlined" color="#fff" />,
  ...inputProps
}) => {
  const checkboxRef = useRef(null)

  useEffect(() => {
    if (checkboxRef.current) {
      checkboxRef.current.indeterminate = indeterminate
    }
  }, [indeterminate])

  const checkboxClass = clsx(
    'flex items-center relative m-0 p-0 text-base leading-normal cursor-pointer select-none align-middle',
    addClass,
    {
      'cursor-not-allowed [&_svg_path]:fill-grey-80': disabled,
    }
  )

  const uniqId = id ?? name ?? 'checkbox'

  const handleChange = e => {
    if (disabled) {
      return // Prevent the onChange if disabled is true
    }
    onChange(e.target.checked)
  }

  return (
    <label
      className={checkboxClass}
      htmlFor={uniqId}
      data-testid={uniqId}
      onClick={e => e.stopPropagation()}
    >
      <input
        id={uniqId}
        ref={checkboxRef}
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={handleChange}
        className="absolute w-0 h-0 opacity-0"
        {...inputProps}
      />

      <div
        data-testid={`${uniqId}-custom`}
        className={clsx('rounded w-[17px] h-[17px] border', {
          'border-theme-50 bg-theme-50': (checked || indeterminate) && !disabled,
          'border-grey-95 bg-grey-95': disabled && (checked || indeterminate),
          'border-grey-90 bg-grey-90': disabled && !checked && !indeterminate,
          'border-grey-70 bg-white': !disabled && !checked && !indeterminate,
        })}
        {...(backgroundColor &&
          (checked || indeterminate) && {
            style: {
              backgroundColor: backgroundColor,
              borderColor: backgroundColor,
            },
          })}
      >
        {checked && checkIcon}

        {indeterminate && <Icon name="minus" size="small" style="outlined" color="#fff" />}
      </div>

      {label && (
        <span
          className={clsx('inline-block relative mb-0 pl-3 type-body-2', {
            'text-theme-50': checked,
          })}
        >
          {label}
        </span>
      )}
    </label>
  )
}

Checkbox.propTypes = {
  id: PropTypes.string, // The ID of the checkbox, useful for accessibility and testing
  checked: PropTypes.bool, // Whether the checkbox is checked or not
  indeterminate: PropTypes.bool, // Whether the checkbox is in an indeterminate state
  onChange: PropTypes.func.isRequired, // Callback when the checkbox state changes
  name: PropTypes.string, // Name attribute for the checkbox input
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]), // Label to display next to the checkbox
  disabled: PropTypes.bool, // Whether the checkbox is disabled
  addClass: PropTypes.string, // Additional class(es) to apply to the checkbox
  backgroundColor: PropTypes.string, // Background color for the checkbox when checked or indeterminate
  checkIcon: PropTypes.node, // Custom icon to display when checked
  inputProps: PropTypes.object, // Additional input attributes
}

// Add default props
Checkbox.defaultProps = {
  checked: false,
  indeterminate: false,
  disabled: false,
  checkIcon: <Icon name="check" size="small" style="outlined" color="#fff" />,
}

export default Checkbox
